import { useEffect, useMemo, useState } from "react";
import WeekDaysSelector from "../WeekDaysSelector";
import Select from "react-select";

const fmt12h = (s) => {
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const h12 = ((hh + 11) % 12) + 1;
  const ampm = hh < 12 ? "AM" : "PM";
  return `${h12}:${mm.toString().padStart(2, "0")} ${ampm}`;
};
const toSec = (h, m) => h * 3600 + m * 60;
const normalize = (s, e) => [s, e <= s ? e + 86400 : e];
const overlaps = (aStart, aEnd, bStart, bEnd) => {
  let [A1, A2] = normalize(aStart, aEnd);
  let [B1, B2] = normalize(bStart, bEnd);
  const o1 = A1 < B2 && B1 < A2;
  const o2 = A1 + 86400 < B2 + 86400 && B1 + 86400 < A2 + 86400;
  const o3 = A1 < B2 + 86400 && B1 + 86400 < A2;
  return o1 || o2 || o3;
};

const PlantShiftWizard = (props) => {
  const isDefault = props.index === -1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [shiftName, setShiftName] = useState(
    isDefault ? "Default shift" : "New shift"
  );
  const [tempShiftName, setTempShiftName] = useState(shiftName);
  const [runsOn, setRunsOn] = useState(props.runs_on || 62);
  const [startSec, setStartSec] = useState(isDefault ? 8 * 3600 : 0);
  const [durSec, setDurSec] = useState(isDefault ? 9 * 3600 : 8 * 3600);
  const endSec = useMemo(() => (startSec + durSec) % 86400, [startSec, durSec]);
  const crossesMidnight = useMemo(
    () => startSec + durSec >= 86400,
    [startSec, durSec]
  );

  useEffect(() => {
    const incoming = (props.allShifts || []).find(
      (s) => s.id === props.shiftId
    );
    if (incoming) {
      setShiftName(incoming.name || shiftName);
      setRunsOn(incoming.runs_on ?? runsOn);
      const s = Number(incoming.start ?? 0);
      const e = Number(incoming.end ?? 0);
      setStartSec(s);
      const d = (e - s + 86400) % 86400;
      setDurSec(d || 1);
    }
  }, [props.shiftId, props.allShifts]);

  const pushChangeUp = (next = {}) => {
    if (props.onShiftChange && props.shiftId) {
      props.onShiftChange({
        id: props.shiftId,
        name: next.name ?? shiftName,
        start: next.start ?? startSec,
        end: next.end ?? endSec,
        runs_on: next.runs_on ?? runsOn,
      });
    }
  };

  const handleNameEdit = () => {
    setTempShiftName(shiftName);
    setIsEditingName(true);
  };
  const handleNameSave = () => {
    const n = (
      tempShiftName || (isDefault ? "Default shift" : "New shift")
    ).trim();
    setShiftName(n);
    setIsEditingName(false);
    pushChangeUp({ name: n });
  };
  const handleNameCancel = () => setIsEditingName(false);

  const handleWeekDaysChange = (value) => {
    setRunsOn(value);
    pushChangeUp({ runs_on: value });
  };

  const startTimeOptions = useMemo(() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      for (let m of [0, 30]) {
        const s = toSec(h, m);
        opts.push({ value: s, label: fmt12h(s) });
      }
    }
    return opts;
  }, []);
  const durationOptions = useMemo(() => {
    const opts = [];
    for (let m = 15; m <= 24 * 60; m += 15) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      const label =
        h > 0 && mm > 0
          ? `${h} hr ${mm} min`
          : h > 0
          ? `${h} hr${h === 1 ? "" : "s"}`
          : `${mm} min`;
      opts.push({ value: m * 60, label });
    }
    return opts;
  }, []);

  const applyTimes = (nextStart, nextDur) => {
    const nextEnd = (nextStart + nextDur) % 86400;

    const others = (props.allShifts || []).filter(
      (s) => s.id !== props.shiftId
    );
    for (const s of others) {
      if (overlaps(nextStart, nextEnd, s.start ?? 0, s.end ?? 0)) {
        if (props.onValidationChange) props.onValidationChange(true);
        return false;
      }
    }

    setStartSec(nextStart);
    setDurSec(nextDur);
    if (props.onValidationChange) props.onValidationChange(false);
    pushChangeUp({ start: nextStart, end: nextEnd });
    return true;
  };

  return (
    <div className="border p-2 mt-2">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          {isEditingName ? (
            <>
              <input
                type="text"
                value={tempShiftName}
                onChange={(e) => setTempShiftName(e.target.value)}
                className="form-control d-inline-block w-auto"
              />
              <i
                className="fa fa-check ms-2 cursor-pointer"
                onClick={handleNameSave}
              ></i>
              <i
                className="fa fa-times text-danger ms-2 cursor-pointer"
                onClick={handleNameCancel}
              ></i>
            </>
          ) : (
            <>
              <span className="fw-normal">{shiftName}</span>
              <i
                className="fa fa-pen ms-2 cursor-pointer"
                onClick={handleNameEdit}
              ></i>
              {props.index >= 0 && (
                <i
                  className="fa fa-trash ms-2 cursor-pointer"
                  onClick={props.onDelete}
                ></i>
              )}
            </>
          )}
        </div>

        <WeekDaysSelector
          shiftId={props.shiftId}
          initialValue={runsOn}
          onChange={handleWeekDaysChange}
        />
      </div>

      <div className="border p-2">
        <div className="mb-2">Timings</div>
        <div className="row g-2">
          <div className="col-md-6">
            <label className="form-label">Start time</label>
            <Select
              inputId={`start_time_${props.shiftId}`}
              options={startTimeOptions}
              value={startTimeOptions.find((o) => o.value === startSec)}
              onChange={(o) => o && applyTimes(o.value, durSec)}
              isClearable={false}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Duration</label>
            <Select
              inputId={`duration_${props.shiftId}`}
              options={durationOptions}
              value={durationOptions.find((o) => o.value === durSec) || null}
              onChange={(o) => o && applyTimes(startSec, o.value)}
              isClearable={false}
            />
          </div>
        </div>

        <div className="mt-2 small">
          <strong>Preview:</strong> {fmt12h(startSec)} → {fmt12h(endSec)}{" "}
          {crossesMidnight && (
            <span className="badge bg-secondary">next day</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlantShiftWizard;
